import BindingContext from '@/BindingContext';
import ComponentManager from '@/Management/ComponentManager';
import ControlViewModel from '@/ViewModels/ControlViewModel';
import ViewModel from '@/ViewModels/ViewModel';
import ControlTuple from '@/Controls/ControlTuple';
import RADApplication from '@/Management/RADApplication';
import ResourceManager from '@/Management/ResourceManager';
import Utilities from '@/Utility/Utilities';

type ItemCreationFunction<TItem, TResult> = (i: TItem) => TResult | Promise<TResult>;

/**
* Repräsentiert die Oberklasse aller Controls. Setzt Standard-Properties und bietet Funktionalität
* zur erleichterten Control-Entwicklung
*/
export default class Control extends BindingContext {
    /**
    * Gibt an, ob die Instanz disposed werden kann
    */
    public isDisposable: boolean;

    /**
     * Gibt an, ob die Properties der Instanz disposed werden können
     */
    public disposeProperties: boolean;

    /**
     * Das Widget, welches für Kendo-Controls benötigt wird
     */
    public widget: KnockoutObservable<any>;

    /**
     * Setzt das ViewModel als Context im Control
     */
    public context: ControlViewModel;

    /**
     * Definiert eine Liste mit registrierten Key-Events
     */
    public keyEvents: Array<any>;

    public timeout: number;
    public tuple: ControlTuple;

    public constructor(viewModel: ControlViewModel, element?: JQuery<HTMLElement>) {
        super();

        if (viewModel == null || 'undefined' === typeof viewModel.isDisposed) {
            throw new Error('Dem Control wurde kein ControlViewModel übergeben!');
        }

        this.undisposeProperties.push('context');
        this.undisposeProperties.push('viewModel');

        this.isDisposable = true;
        this.disposeProperties = true;
        this.widget = this.observableOrDefault(null).extend({ notify: 'always' });
        this.context = viewModel;
        this.timeout = 50;
        this.keyEvents = [];
    }

    /**
     * Fügt das Dispose-Callback von Knockout hinzu, sodass nach Entfernen
     * des Controls aus dem DOM auch die Control-Instanz disposed wird
     * @param element
     */
    protected addDisposeCallback(element: JQuery<HTMLElement>): void {
        element?.each((index, element) => {
            ko.utils.domNodeDisposal.addDisposeCallback(element, () => {
                if (!this.isDisposedOrDisposing()) {
                    this.dispose();
                }
            });
        });
    }

    public getControl(): JQuery {
        throw new Error('Die Funktion "getControl" wurde nicht implementiert');
    }

    /**
    * Erzeugt das ControlTuple und das Control
    */
    public getTuple() {
        if (this.context.isDisposed === true) {
            return null;
        }

        if (this.tuple)
            return this.tuple;

        const $control = this.getControl();
        this.tuple = new ControlTuple($control);

        this.addDisposeCallback(this.tuple.control);

        return this.tuple;
    }

    /**
    * Wendet Bindings auf ein DOM-Element an
    */
    public applyBindings(element: any, bindings: Object): void {
        if (element[0] == null) {
            throw 'Bindings können nicht angewendet werden. Element ist null.';
        }

        ko.applyBindingsToNode(element[0], $.extend({ uiElement: this }, bindings));
    }

    /**
    * Registriert ein Template in einem Kontext
    */
    public registerTemplate(string: string, context: ViewModel, callback?: any): number {
        return this.registerTemplateAsync(string, context, this.timeout, callback);
    }

    /**
    * Registriert ein Template in einem Kontext (sofort)
    */
    private registerTemplateAsync(string:string , context: any, delay: number, callback?: Function): number {
        return this.async(() => {
            if (this.isDisposed)
                return;

            const element = $(string).appendTo(context.empty());

            if (callback) {
                callback(element);
            }
        }, delay);
    }

    /**
    * Registriert ein Template in einem Kontext (sofort)
    */
    private registerKoElementAsync(koElement: KnockoutJsxElement, context: JQuery, delay: number, callback?: Function): number {
        return this.async(() => {
            if (this.isDisposed)
                return;

            const node = ko.jsx.render(koElement).node;
            const element = $(node).appendTo(context.empty());

            if (callback) {
                callback(element);
            }
        }, delay);
    }

    /**
    * Wendet Bindings auf ein Template an
    */
    public applyTemplate(string: string, control: JQuery, callback?: any): number {
        return this.applyTemplateAsync(string, control, this.timeout, callback);
    }

    /**
     * Wendet Bindings auf ein Template an (asynchron)
     */
    public applyTemplateAsync(string: string, context: ViewModel | JQuery, delay: number, callback?: any): number {
        return this.registerTemplateAsync(string, context, delay, (element) => {
            ko.applyBindings({
                uiElement: this,
                viewModel: this.context,
                RADApplication: RADApplication,
                ResourceManager: ResourceManager
            }, element[0]);

            if (callback != null) {
                callback();
            }
        });
    }

    /**
     * Wendet Bindings auf ein Template an (asynchron)
     */
    public renderKoElementAsync(koElement: KnockoutJsxElement, context: JQuery, delay: number, callback?: any): number {
        return this.registerKoElementAsync(koElement, context.empty(), delay, (element) => {
            ko.applyBindings({
                uiElement: this,
                viewModel: this.context,
                RADApplication: RADApplication,
                ResourceManager: ResourceManager
            }, element[0]);

            if (callback != null) {
                callback();
            }
        });
    }

    /**
     * Registriert eine Funktion auf einem Ctrl+key-Press-Event
     */
    public registerKeyEvent(key: any, element: any, func: any) {
        const _internalFunc = (...args) => {
            if (args[0].preventDefault)
                args[0].preventDefault();

            func.apply(this, args);
        };

        const reg = {
            element: element,
            func: _internalFunc
        };

        this.keyEvents.push(reg);

        element.on('keydown', null, key, _internalFunc);

        return {
            dispose: () => {
                element.off('keydown', null, _internalFunc);
                const idx = this.keyEvents.indexOf(reg);
                if (idx != -1) {
                    this.keyEvents.splice(idx, 1);
                }
            }
        };
    }

    /**
    * Definiert einen Callback für ein erzeugtes Kendo-Widget
    */
    public onWidgetCreated(callback: (widget: any) => void): void {
        this.defineSubscription(this.widget, (widget) => {
            if (this.isDisposed === false && this.context != null && this.context.isDisposed === false) {
                callback(widget);
            }
        });
    }

    public addLabelReference(label: JQuery<HTMLElement>): JQuery<HTMLElement> {
        return null;
    }

    /**
     * Erzeugt eine neue Instanz eines Controls anhand einer Komponentenbezeichnung
     */
    public static createInstance(component: string, viewModel: ControlViewModel, element: JQuery): Control {
        const Component: any = ComponentManager.getControl(component);

        if (Component == null) {
            throw 'Control-Komponente "' + component + '" wurde nicht gefunden.';
        }

        return new Component(viewModel, element);
    }

    protected async createItemsAsync<TItem, TResult>(items: TItem[], createFunc: ItemCreationFunction<TItem, TResult>): Promise<TResult[]> {
        if (this.isDisposed || !items?.length)
            return Promise.resolve([]);

        const promises: Promise<TResult>[] = [];
        for (const item of items) {
            promises.push(this.createItemAsync(item, createFunc));
        }
        const results = await Promise.all(promises);
        return results.filter(r => r != null);
    }

    protected createItemAsync<TItem, TResult>(item: TItem, createFunc: ItemCreationFunction<TItem, TResult>): Promise<TResult> {
        return new Promise<TResult>((resolve, reject) => {
            window.setTimeout(async () => {
                if (this.isDisposed) {
                    resolve(null);
                    return;
                }

                try {
                    const result = await createFunc.bind(this)(item);
                    resolve(result);
                } catch (error) {
                    reject(error);
                }
            });
        });
    }

    /**
    * Disposed das Control
    */
    protected disposeInternal() {
        if (!Utilities.isNullOrUndefined(this.keyEvents)) {
            for (let i = 0; i < this.keyEvents.length; i++) {
                this.keyEvents[i].element.off('keydown', this.keyEvents[i].func);
            }
        }

        super.disposeInternal();
    }
}